import { mdiClose } from '@mdi/js';
import { Formik, FormikProps } from 'formik';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import * as Yup from 'yup';
import { ComposeControls, ComposeError } from '.';
import { MessagingContext } from '../../../services/';
import { SidePanel } from '../side-panel';
import { MessageFields, IMessageFieldsValues } from '../message-fields';
import { BaseSelect } from '../../base/select';
import * as S from './compose-panel.styles';

const TEMPLATE_CATEGORIES = [
    { value: 'Coding', label: 'Coding' },
    { value: 'Eligibility', label: 'Eligibility' },
    { value: 'Claim Submission', label: 'Claim Submission' },
    { value: 'Credentialing', label: 'Credentialing' },
    { value: 'AR', label: 'AR' },
    { value: 'Payment Posting', label: 'Payment Posting' },
    { value: 'Other', label: 'Other' },
];

const TEMPLATES = [
    { value: "Hi there,\n\nCPT code (insert denied code) is denied as (insert denial code/reason). This code is considered bundled to another code that was billed on the same day.\n\nPlease review the coding and send a modifier if applicable to re-bill this code.\n\nThank you.", label: "Bundling" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason). According to the payer, this should be billed with the appropriate code for these services.\n\nThis denial means that the diagnosis provided does not match or support the procedure that was performed. In other words, there is a discrepancy between the diagnosis code and the procedure code submitted for reimbursement.\n\nPlease verify the correct diagnosis/procedure codes for this claim. \n\nThank you.", label: "DX" },
    { value: "Hi there,\n\nProcedure code (insert denied code) was denied as (insert denial code/reason).\n\nPlease verify if the said procedure code on this claim is correct. \n\nThank you.", label: "CPT" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason). \nThis denial means that there is a discrepancy between the modifier and the procedure code submitted for reimbursement.\n\nPlease verify the correct modifier and place of service of this claim. \n\nThank you.", label: "Modifier" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nPlease send the correct place of service.\n\nThank you.", label: "POS" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason). \nThis denial means that there is a discrepancy between the modifier and the procedure code submitted for reimbursement.\n\nPlease verify the correct modifier of this claim. \n\nThank you.", label: "CPT/ICD10/Modifier Issue" },
    { value: "Hi There,\n\nThe claim was denied as (insert denial code/reason).\n\nMay we please verify if this document was provided before the service rendered? \n\nThank you.", label: "Documentation Insufficient" },
    { value: "Hi There,\n\nThe claim was denied as (insert denial code/reason). This means that the insurance company has refused to pay for a medical treatment or procedure because they consider it to be \"experimental\" or \"investigational,\" meaning it is not yet widely accepted by the medical community as proven effective and safe for the intended use. \n\nPlease advise if we could close this claim.\n\nThank you.", label: "Experimental" },
    { value: "Hi there,\n\nThe insurance reduced the payment for this CPT code (insert CPT code). The payer added a modifier 51 to the code and it was paid 50% of the CMS Fee because a similar procedure code was billed on the same day and same claim (ex. 20610-LT and 20610-LT,59). \n\nPlease advise if we could go ahead and accept the payment and bill the remaining patient responsibility if there's any.\n\nThank you.", label: "Reduction in Pay on Multiple Procedures" },
    { value: "Hi There,\n\nThe claim was denied as (insert denial code/reason).\n\nDo you have other insurance information of the patient? \n\nThank you.", label: "Non-Covered Charges" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason). The claim or service may be denied because it is not considered medically necessary. \n\nPlease verify and advise on how to proceed. \n\nThank you.", label: "Not Medical Necessity" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason). MUEs are the maximum number of units of service that a provider can report for a given CPT code. \n\nPlease verify the number of units and advise on how to proceed. \n\nThank you.", label: "MUE Frequency Not Supported" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nThis means the patient has another primary insurance that will cover this claim. Please send the correct primary insurance.\n\nThank you.", label: "COB" },
    { value: "Hi there,\n\nWe are unable to submit this claim due to failed eligibility.\n\n(insert eligibility error from support tool)\n\nPlease check if we have the correct insurance details, patient's first & last name, date of birth, and member ID.\n\nThank you.", label: "Not Active / Need Updated Insurance Info" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nThis means the patient has a managed care plan insurance that will cover this claim. Please send the correct primary insurance.\n\nThank you.", label: "Capitation and/or HMO" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nPlease send the correct primary insurance.\n\nThank you.", label: "Other Insurance Primary" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nThis means that the maximum benefit allowed for a specific time period or occurrence has been reached. This indicates that the insurance coverage or plan has a limit on the amount of money it will pay for a particular service or treatment within a given time-frame.\n\nUpon checking, there is a service under this patient on (insert previous DOS). Please confirm if we can close this claim instead? \n\nThank you.", label: "Max Benefits Reached" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason). The claim or service may be denied because it is not covered by the patient's insurance plan or is considered medically unnecessary. This could be due to limitations in the insurance policy or failure to meet specific criteria for coverage.\n\nPlease verify and advise on how to proceed. \n\nThank you.", label: "Charges Not Covered" },
    { value: "Hi there,\n\nThis is a worker's comp claim and we need the valid authorization form for this date of service.\n\nPlease send the auth form to bill this claim.\n\nThank you.", label: "Work Comp Claim" },
    { value: "Hi there,\n\nPlease verify if this claim is valid.\n\nUpon checking, there's an identical claim (insert duplicate claim ID).\n\nShould we close the claim? \n\nThank you.", label: "Duplicate Claim" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nPlease provide us correct place of service for this claim. \n\nThank you.", label: "Place of Service Issue" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason) An attachment/other documentation is required to adjudicate this claim/service. The payer is requesting medical records to process the claim.\n\nPlease provide us with the patient's medical records. \n\nThank you.", label: "Medical Records" },
    { value: "Hi there,\n\nWe cannot create this claim because it's missing the (insert missing info, ex: provider NPI , missing member ID).\n\nPlease send the needed information. \n\nThank you.", label: "Missing Info on Claim" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nThis means that an assistant physician is not covered for this claim. Please advise if we could close this claim. \n\nThank you.", label: "Multiple Providers on Same Claim Not Payable" },
    { value: "Hi there, \n\nThis claim was denied due to timely filing. We sent appeal document for the claim but we received a letter stating it was upheld. No additional payment was provided for this claim.\n\nShould we close this? It's evident that it was transmitted late, resulting in a lapse of timely filing. \n\nThank you.", label: "Timely Filing" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nPlease provide us the details of the referring provider to process the claim. \n\nThank you.", label: "Referring Provider Missing" },
    { value: "Hi there,\n\nCPT code (insert denied code) is denied as (insert denial code/reason). This code is within the global period of another related service provided. During the global period, a provider cannot bill separately for related visits. Modifiers can be used to indicate services that are not part of the global period. \n\nPlease review the coding and send a modifier if applicable to re-bill this code.\n\nThank you.", label: "Global Period" },
    { value: "Hi there,\n\nThe claim was denied as (insert denial code/reason).\n\nPlease verify if the rendering provider is credentialed/enrolled with the payer. \n\nThank you.", label: "Provider Issues" },
    { value: "Hi there,\n\nThe claim was processed as Out Of Network due to (insert reason). Please verify if the provider is credentialed.\n\nShould we bill the patient for this claim or close it out instead? \n\nThank you.", label: "Out of Network" },
    { value: "Hi there,\n\nThe claim was processed as Out Of Network due to (insert reason). Please verify if we are credentialed with this payer\n\nShould we bill the patient for this claim or close it out instead? \n\nThank you.", label: "Facility Issues" },
    { value: "Hi There,\n\nThe claim was denied as (insert denial code/reason).\n\nMay we please verify if this document was provided before the service rendered? You may also send us a copy of the prior auth document. \n\nThank you.", label: "Prior Auth Issue" },
    { value: "Hi There,\n\nAfter the appeal, the claim was denied as (insert denial/reason). The submitted medical records do not support the charges billed for this code.\n\nDo we have other documentation that can support the claim or should we close this out? \n\nThank you.", label: "Appeal Denied" },
    { value: "Hi there,\n\nThe claim is underpaid and appeal was sent. However, the payer responded that (insert payer's response)\n\nShould we accept this payment and collect the patient's responsibility? \n\nThank you.", label: "Prior Payer Payment/Adjustment Stands" },
    { value: "Hi there,\n\nThe claim payment was made to patient/insured/responsible party. This means that the insurance company has sent the payment directly to the patient, rather than to the healthcare provider, which usually happens when the patient is responsible for the full cost of the service, like in cases of non-covered services, out-of-network providers, or if there was an error with the insurance claim submission. \n\nMay you please confirm to the patient if they receive the payment? and should we go ahead and bill this claim to the patient?\n\nThank you.", label: "Payment Made to Patient" },
    { value: "Hi there,\n\nThe claim was successfully voided.\n\nCan we close the claim now? \n\nThank you.", label: "Voided Claim" },
    { value: "Hi there,\n\nThe patient is disputing the charges due to (insert reason of patient dispute).\n\nPlease confirm if we should continue billing the patient. \n\nThank you.", label: "Patient Responsibility/Dispute" },
    { value: "Hi there,\n\nThe patient's payment has been refunded.\n\nThank you.", label: "Refund / Record Refund" },
    { value: "Hi there,\n\nWe do not have a copy of the EOB for this claim. Do you happen to have a copy of it? If so, please send it so we can record and post the payment for this claim.\n\nThank you.", label: "Payment Recording Needed" },
    { value: "Hi there,\n\nWe are unable to create this claim because the number of units for the procedure code is empty.\n\nPlease send the correct units.\n\nThank you.", label: "Not Classified" },
    { value: "Hi there,\n\nWe are unable to submit this claim due to the following error.\n\n(insert error from support tool)\n\nPlease check if all the claim information is correct.\n\nThank you.", label: "Transmit Issue" }
];

const schema = Yup.object().shape({
    claimIds: Yup.array().of(Yup.string()),
    subject: Yup.string().min(0).max(80).required('Subject is required.'),
    body: Yup.string().required('Message body is required'),
    attachments: Yup.array(),
});

interface IDraft {
    claimIds: string[];
    subject: string;
    body: string;
    note: boolean;
    attachments: File[];
    messageFields: IMessageFieldsValues;
}

export interface IComposePanelProps {
    className?: string;
    claimId?: string;
    // Make it not possible to change the claim. ClaimId is required if forceClaim === true
    forceClaim?: boolean;
    hideNote?: boolean;
    subject?: string;
    body?: string;
}

export const ComposePanel: React.FunctionComponent<IComposePanelProps> = (
    props: IComposePanelProps,
): React.ReactElement => {
    const { className, forceClaim, hideNote } = props;

    const context = useContext(MessagingContext);
    const isSupportApp = context.account?.supportGrant === true;
    const [locked, setLocked] = useState(false);
    const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<{ value: string; label: string } | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<{ value: string; label: string } | null>(null);

    // Filter templates based on selected category
    const getFilteredTemplates = (): Array<{ value: string; label: string }> => {
        if (!selectedTemplateCategory) {
            return [];
        }

        const categoryRanges: Record<string, { start: number; end: number }> = {
            'Coding': { start: 0, end: 11 },                    // Bundling to MUE Frequency Not Supported
            'Eligibility': { start: 12, end: 18 },              // COB to Work Comp Claim
            'Claim Submission': { start: 19, end: 26 },         // Duplicate Claim to Global Period
            'Credentialing': { start: 27, end: 29 },            // Provider Issues to Facility Issues
            'AR': { start: 30, end: 31 },                       // Prior Auth Issue to Appeal Denied
            'Payment Posting': { start: 32, end: 37 },          // Prior Payer Payment/Adjustment Stands to Payment Recording Needed
            'Other': { start: 38, end: 39 },                    // Not Classified, Transmit Issue
        };

        const range = categoryRanges[selectedTemplateCategory.value];
        if (!range) {
            return TEMPLATES;
        }

        return TEMPLATES.slice(range.start, range.end + 1);
    };

    const filteredTemplates = getFilteredTemplates();

    const initial: IDraft = {
        claimIds: props.claimId ? [props.claimId] : [],
        subject: props.subject || '',
        body: props.body || '',
        note: !hideNote,
        attachments: [],
        messageFields: {
            priority: undefined,
            dueDate: undefined,
            categoryIds: [],
            assignedUserIds: [],
        },
    };

    const claimSelected = (formik: FormikProps<IDraft>): ((id: string) => Promise<void>) => {
        return async (id: string): Promise<void> => {
            const currentClaims = formik.values.claimIds || [];
            if (!currentClaims.includes(id)) {
                formik.setFieldValue('claimIds', [...currentClaims, id]);
            }
        };
    };

    const bulkClaimsSelected = (formik: FormikProps<IDraft>): ((ids: string[]) => Promise<void>) => {
        return async (ids: string[]): Promise<void> => {
            const currentClaims = formik.values.claimIds || [];
            // Filter out IDs that are already in the list
            const newClaims = ids.filter(id => !currentClaims.includes(id));
            if (newClaims.length > 0) {
                formik.setFieldValue('claimIds', [...currentClaims, ...newClaims]);
            }
        };
    };

    const handleRemoveClaim = (formik: FormikProps<IDraft>) => async (claimId: string): Promise<void> => {
        const currentClaims = formik.values.claimIds || [];
        formik.setFieldValue('claimIds', currentClaims.filter(id => id !== claimId));
    };

    const handleInputChange = (formik: FormikProps<IDraft>): ((subject: string) => void) => {
        return (subject: string): void => {
            formik.setFieldValue('subject', subject);
        };
    };

    const handleTextareaChange = (formik: FormikProps<IDraft>): ((body: string, attachments: File[]) => void) => {
        return (body: string, attachments: File[]): void => {
            formik.setFieldValue('body', body);
            formik.setFieldValue('attachments', attachments);
        };
    };

    const onChangeNote = (formik: FormikProps<IDraft>): ((value: boolean) => void) => {
        return (value: boolean): void => {
            formik.setFieldValue('note', value);
        };
    };

    const handleMessageFieldsChange = (formik: FormikProps<IDraft>): ((values: IMessageFieldsValues) => void) => {
        return (values: IMessageFieldsValues): void => {
            formik.setFieldValue('messageFields', values);
        };
    };

    const handleTemplateCategoryChange = (option: any) => {
        setSelectedTemplateCategory(option);
        // Clear template when category changes
        setSelectedTemplate(null);
    };

    const handleTemplateChange = (formik: FormikProps<IDraft>) => (option: any) => {
        setSelectedTemplate(option);
        
        if (option) {
            // Populate body with template value
            formik.setFieldValue('body', option.value);
            
            // Populate subject with "Category - Template Label"
            if (selectedTemplateCategory) {
                const subject = `${selectedTemplateCategory.value} - ${option.label}`;
                formik.setFieldValue('subject', subject);
            }
        }
    };

    const handleTextareaSave = (formik: FormikProps<IDraft>): ((body: string, attachments: File[]) => void) => {
        return (body: string, attachments: File[]): void => {
            formik.setFieldValue('body', body);
            formik.setFieldValue('attachments', attachments);
            handleSave(formik)();
        };
    };

    const handleClose = async (): Promise<void> => {
        await (context.onMessageClose && context.onMessageClose());
    };
    const handleSave = (formik: FormikProps<IDraft>): (() => Promise<void>) => {
        // NB: avoid non-async formik.submitForm for now
        return async (): Promise<void> => {
            const errors = await formik.validateForm();
            if (_.isEmpty(errors)) {
                setLocked(true);
                await handleSubmit(formik.values);
            }
        };
    };
    const handleSubmit = async (values: IDraft): Promise<void> => {
        const { subject, note, claimIds, attachments, messageFields } = values;

        const body = values.body.replace(/([^\n])\n(?!\n)/g, '$1  \n');
        
        const claims = claimIds.map(id => ({ id, entityType: 'CLAIM' }));
        
        // Build links object with all entities
        const links: any = { claims };
        
        if (messageFields.categoryIds && messageFields.categoryIds.length > 0) {
            links.messageCategories = messageFields.categoryIds.map(id => ({ id, entityType: 'MESSAGE_CATEGORY' }));
        }
        
        if (messageFields.assignedUserIds && messageFields.assignedUserIds.length > 0) {
            links.users = messageFields.assignedUserIds.map(id => ({ id, entityType: 'BANK_USER' }));
        }
        
        await (context.onComposeMessage &&
            context.onComposeMessage({
                subject,
                body,
                note,
                links,
                files: attachments,
                priority: messageFields.priority,
                dueDate: messageFields.dueDate ? new Date(messageFields.dueDate + 'T00:00:00Z').toISOString() : undefined,
            }));
        await (context.onMessageClose && context.onMessageClose());
    };

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initial}
            validationSchema={schema}
            validateOnChange={false}
            onSubmit={handleSubmit}>
            {(formik: FormikProps<IDraft>): React.ReactElement => (
                <SidePanel className={className}>
                    <S.Header>
                        <S.Title>Start new message</S.Title>
                        <S.CloseButton variant="fab-light" onClick={handleClose}>
                            <S.CloseIcon path={mdiClose} size={1} />
                        </S.CloseButton>
                    </S.Header>
                    <S.Body>
                        {isSupportApp && (
                            <S.TemplateRow>
                                <S.TemplateColumn>
                                    <S.TemplateLabel>Template Category</S.TemplateLabel>
                                    <BaseSelect
                                        options={TEMPLATE_CATEGORIES}
                                        value={selectedTemplateCategory}
                                        onChange={handleTemplateCategoryChange}
                                        placeholder="Select Category"
                                        disabled={locked}
                                        isClearable={true}
                                    />
                                </S.TemplateColumn>
                                <S.TemplateColumn>
                                    <S.TemplateLabel>Template</S.TemplateLabel>
                                    <BaseSelect
                                        options={filteredTemplates}
                                        value={selectedTemplate}
                                        onChange={handleTemplateChange(formik)}
                                        placeholder="Select Template"
                                        disabled={locked || !selectedTemplateCategory}
                                        isClearable={true}
                                    />
                                </S.TemplateColumn>
                            </S.TemplateRow>
                        )}
                        <S.StyledComposeInput
                            isNote={formik.values.note}
                            disabled={locked}
                            subject={formik.values.subject}
                            onChange={handleInputChange(formik)}
                        />
                        {formik.errors && formik.errors.subject && <ComposeError>{formik.errors.subject}</ComposeError>}
                        <S.StyledComposeTextarea
                            isNote={formik.values.note}
                            disabled={locked}
                            body={formik.values.body}
                            onChange={handleTextareaChange(formik)}
                            onSave={handleTextareaSave(formik)}
                        />
                        {!hideNote && (
                            <S.NoteContainer>
                                <S.NoteToggle
                                    disabled={locked}
                                    value={formik.values.note}
                                    onChange={onChangeNote(formik)}
                                />
                                {formik.values.note ? (
                                    <S.Note>Sending as internal note</S.Note>
                                ) : (
                                    <S.Comment>Sending as provider message</S.Comment>
                                )}
                            </S.NoteContainer>
                        )}
                        {formik.errors && formik.errors.body && <ComposeError>{formik.errors.body}</ComposeError>}
                        <S.StyledMessageFields>
                            <MessageFields
                                values={formik.values.messageFields}
                                onChange={handleMessageFieldsChange(formik)}
                            />
                        </S.StyledMessageFields>
                        <S.StyledClaimsManager
                            claimIds={formik.values.claimIds}
                            onClaimAdd={claimSelected(formik)}
                            onBulkClaimsAdd={bulkClaimsSelected(formik)}
                            onClaimRemove={handleRemoveClaim(formik)}
                        />
                        <ComposeControls locked={locked} onClose={handleClose} onSave={handleSave(formik)} />
                    </S.Body>
                </SidePanel>
            )}
        </Formik>
    );
};
